import {
  IAddProductVariantInput,
  IAttribute,
  ICategory,
  IProductVariant,
  ISubCategory,
  ISubSubCategory,
} from "muminsmart-utils";

export function normalizeIds(items: unknown): string[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }

      if (item && typeof item === "object" && "id" in item) {
        const id = (item as { id?: unknown }).id;
        return typeof id === "string" ? id : "";
      }

      return "";
    })
    .filter((id) => id.length > 0);
}

export function formatVariantAttributeSummary(
  variant: IAddProductVariantInput | undefined,
  attributeNameById: Map<string, string>,
): string {
  return (variant?.variantAttributes ?? [])
    .filter((attribute) => attribute.value)
    .map((attribute) => {
      const name =
        attributeNameById.get(attribute.attributeId) ?? attribute.attributeId;

      return `${name}: ${attribute.value}`;
    })
    .join(" · ");
}

export function getSelectedCategories(
  categories: ICategory[],
  selectedCategoryIds: string[],
): ICategory[] {
  const selectedIds = new Set(selectedCategoryIds);
  return categories.filter((category) => selectedIds.has(category.id));
}

export function getSelectedSubCategories(
  categories: ICategory[],
  selectedSubCategoryIds: string[],
): ISubCategory[] {
  const selectedIds = new Set(selectedSubCategoryIds);
  const selectedSubCategories: ISubCategory[] = [];

  for (const category of categories) {
    for (const subCategory of category.subCategories ?? []) {
      if (selectedIds.has(subCategory.id)) {
        selectedSubCategories.push(subCategory);
      }
    }
  }

  return selectedSubCategories;
}

export function getSelectedSubSubCategories(
  categories: ICategory[],
  selectedSubSubCategoryIds: string[],
): ISubSubCategory[] {
  const selectedIds = new Set(selectedSubSubCategoryIds);
  const selectedSubSubCategories: ISubSubCategory[] = [];

  for (const category of categories) {
    for (const subCategory of category.subCategories ?? []) {
      for (const subSubCategory of subCategory.subSubCategories ?? []) {
        if (selectedIds.has(subSubCategory.id)) {
          selectedSubSubCategories.push(subSubCategory);
        }
      }
    }
  }

  return selectedSubSubCategories;
}

export function getVariantAttributes(
  selectedSubSubCategories: ISubSubCategory[],
): Array<{ attribute: IAttribute; value: string }> {
  const byId = new Map<string, { attribute: IAttribute; value: string }>();

  for (const subSubCategory of selectedSubSubCategories) {
    for (const categoryAttribute of subSubCategory.categoryAttributes ?? []) {
      if (!categoryAttribute.isVariant) {
        continue;
      }

      byId.set(categoryAttribute.attribute.id, {
        attribute: categoryAttribute.attribute,
        value: "",
      });
    }
  }

  return Array.from(byId.values());
}

export function getAttributeNameById(
  selectedSubSubCategories: ISubSubCategory[],
): Map<string, string> {
  const attributeNameById = new Map<string, string>();

  for (const subSubCategory of selectedSubSubCategories) {
    for (const categoryAttribute of subSubCategory.categoryAttributes ?? []) {
      if (!categoryAttribute.isVariant) {
        continue;
      }

      attributeNameById.set(
        categoryAttribute.attribute.id,
        categoryAttribute.attribute.name,
      );
    }
  }

  return attributeNameById;
}

export function getAttributeNameByIdFromVariants(
  variants: IProductVariant[] | undefined,
): Map<string, string> {
  const attributeNameById = new Map<string, string>();

  for (const variant of variants ?? []) {
    for (const variantAttribute of variant.variantAttributes ?? []) {
      const name = variantAttribute.attribute?.name;

      if (name) {
        attributeNameById.set(variantAttribute.attributeId, name);
      }
    }
  }

  return attributeNameById;
}

export function mergeAttributeNameMaps(
  ...maps: Map<string, string>[]
): Map<string, string> {
  const merged = new Map<string, string>();

  for (const map of maps) {
    for (const [attributeId, name] of map) {
      merged.set(attributeId, name);
    }
  }

  return merged;
}

export function getCategoryAttributeById(
  selectedSubSubCategories: ISubSubCategory[],
): Map<string, IAttribute> {
  const attributeById = new Map<string, IAttribute>();

  for (const subSubCategory of selectedSubSubCategories) {
    for (const categoryAttribute of subSubCategory.categoryAttributes ?? []) {
      attributeById.set(
        categoryAttribute.attribute.id,
        categoryAttribute.attribute,
      );
    }
  }

  return attributeById;
}
